import { useEffect, useState } from "react";
import { AnimatePresence } from "framer-motion";
import { ModalShell } from "@/components/ui/ModalShell";
import { X } from "lucide-react";
import { useTranslation } from "@/i18n/I18nProvider";
import { listMyVisits, type MyVisit } from "@/lib/visits";
import { FeedbackForm } from "./FeedbackForm";

/**
 * Delayed "rate your visit" prompt.
 *
 * A while after a conversion, nudge the diner to rate it — star-first and
 * skippable, reusing FeedbackForm. On the web there's no reliable push, so this
 * fires on the next foreground session for an eligible (aged, un-reviewed,
 * not-recently-prompted) visit. A localStorage throttle keeps it from nagging;
 * the Feedbacks tab remains the always-available path.
 */

const DELAY_MS = 2 * 60 * 60 * 1000; // don't ask until ~2h after the visit
const THROTTLE_MS = 12 * 60 * 60 * 1000; // at most one prompt per this window
const STORE_KEY = "resto_feedback_prompt";

interface PromptState {
  dismissed: number[];
  lastAt: number;
}

function readStore(): PromptState {
  try {
    const raw = window.localStorage.getItem(STORE_KEY);
    const parsed = raw ? (JSON.parse(raw) as Partial<PromptState>) : {};
    return { dismissed: Array.isArray(parsed.dismissed) ? parsed.dismissed : [], lastAt: parsed.lastAt ?? 0 };
  } catch {
    return { dismissed: [], lastAt: 0 };
  }
}

function writeStore(next: PromptState): void {
  try {
    window.localStorage.setItem(STORE_KEY, JSON.stringify(next));
  } catch {
    /* private mode — the prompt simply reappears next window */
  }
}

export function RatingPrompt() {
  const { t } = useTranslation();
  const [visit, setVisit] = useState<MyVisit | null>(null);

  useEffect(() => {
    let cancelled = false;
    // Small delay so this never competes with the feed's first paint.
    const id = window.setTimeout(() => {
      const store = readStore();
      if (Date.now() - store.lastAt < THROTTLE_MS) return;
      void listMyVisits().then((visits) => {
        if (cancelled) return;
        const now = Date.now();
        const eligible = visits.find(
          (v) =>
            v.feedback_at == null &&
            !store.dismissed.includes(v.id) &&
            now - new Date(v.detected_at).getTime() >= DELAY_MS,
        );
        if (eligible) {
          setVisit(eligible);
          writeStore({ ...store, lastAt: now });
        }
      });
    }, 4000);
    return () => {
      cancelled = true;
      window.clearTimeout(id);
    };
  }, []);

  function dismiss() {
    if (visit) {
      const store = readStore();
      writeStore({ ...store, dismissed: [...store.dismissed, visit.id] });
    }
    setVisit(null);
  }

  return (
    <AnimatePresence>
      {visit && (
        <ModalShell
          key="rating-backdrop"
          zClass="z-[115]"
          motionStyle="rise"
          onBackdropClick={dismiss}
          aria-label={t("RATING_PROMPT_TITLE")}
          backdropClass="items-end p-3 sm:items-center"
          panelClass="max-w-md p-5 sm:p-6"
        >
            <div className="mb-4 flex items-start justify-between gap-3">
              <div>
                <h2 className="font-display text-lg tracking-tight text-zinc-50 light:text-zinc-900">
                  {t("RATING_PROMPT_TITLE")}
                </h2>
                <p className="mt-1 text-sm text-zinc-400 light:text-zinc-600">
                  {t("RATING_PROMPT_BODY", { venue: visit.venue_name })}
                </p>
              </div>
              <button
                type="button"
                onClick={dismiss}
                aria-label={t("FEEDBACK_SKIP")}
                className="grid h-8 w-8 shrink-0 place-items-center rounded-full border border-white/15 light:border-black/15 bg-zinc-900/80 light:bg-white/80 text-zinc-400 light:text-zinc-600 transition hover:border-white/50 light:hover:border-black/40 hover:text-zinc-50 light:hover:text-zinc-900"
              >
                <X className="h-4 w-4" aria-hidden />
              </button>
            </div>
            <FeedbackForm visitId={visit.id} onSubmitted={() => setVisit(null)} onSkip={dismiss} />
        </ModalShell>
      )}
    </AnimatePresence>
  );
}
